// /club のエントリポイント。
// 1) /api/me でログイン状態を判定。未ログインなら同じフォーム上で合言葉欄を表示
//    (= / にリダイレクトしない、club 単独で完結)
// 2) 入室フォームで名前 (+ 必要なら合言葉) + マイク許可
// 3) AudioContext 起動 + audio-graph 構築 + WHEP 接続
// 4) Phaser シーン起動

import Phaser from "phaser";

import { setupNameChangeModal } from "../shared/name-change-modal";
import { ParticipantsList } from "../shared/participants";
import { startVisibilityTracker, type VisibilityTracker } from "../shared/visibility-tracker";
import { createGraph, type ClubGraph } from "./audio-graph";
import { AVATARS } from "./avatars";
import { Live } from "./live";
import { MAP, type Zone } from "./map";
import { Net } from "./net";
import { ClubScene } from "./scene";

let participantsList: ParticipantsList | null = null;
let visibilityTracker: VisibilityTracker | null = null;

async function fetchMe(): Promise<{ name: string } | null> {
  try {
    const res = await fetch("/api/me", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return (await res.json()) as { name: string };
  } catch {
    return null;
  }
}

function setStatus(text: string): void {
  const el = document.getElementById("club-status");
  if (el) el.textContent = text;
}

// 招待 URL (= `/club?invite=<token>`) で来たゲストの token を保持。login submit
// 時に /api/login の body にこの token を含めて送り、サーバが session.inviter を
// 焼き込む。入室成功で null クリア。
let inviteToken: string | null = null;

// ChatRoom (= /ws) との WebSocket。/club は ClubRoom (= /club/ws) と別に
// この WS にも繋いで、/ と同じ chat / SE / HEAT を共有する。
let chatWs: WebSocket | null = null;
// SE 用のバッファキャッシュ + AudioContext (= setupChatAndSE で初期化される)。
const seBuffers = new Map<string, AudioBuffer>();
let seAudioCtx: AudioContext | null = null;
// SE マスターゲイン (= ホーンが音楽より大きく耳を裂くのを抑える)。
// `/` 側の `SE_GAIN_DEFAULT` (= main.ts) と同じ 0.15 に揃える。/club は調整 UI を
// 持たないので固定値。視聴者ごとの音量調整は `/` 側のみ提供。
let seGain: GainNode | null = null;
const SE_GAIN_DEFAULT = 0.15;
// ホーン距離減衰: 同じ /club 内で別 player がホーンを鳴らした時、自分との距離で
// gain を下げる。inverse rolloff `REF / (REF + dist * ROLLOFF)`、min で底打ち。
//   dist=100 → 0.83, dist=500 → 0.45, dist=1500 → 0.21, dist=2500 → 0.14
// world max diagonal は ~2480px なので、遠隔の端と中央で 4〜5 倍の音量差が出る。
const HORN_DIST_REF = 500;
const HORN_DIST_ROLLOFF = 1.0;
const HORN_DIST_MIN_GAIN = 0.1;
/** 自分の現在位置を返すコールバック (= boot で net.me を見るクロージャを渡す)。
 *  setupChatAndSE → playSE で距離計算に使う。/club 以外 (= /) では null。 */
type GetSelfPos = (() => { x: number; y: number } | null) | null;
let getSelfPos: GetSelfPos = null;
const SE_FILES = { horn: "/se/horn.mp3" } as const;
type SEId = keyof typeof SE_FILES;

interface ChatServerMessage {
  type:
    | "CHAT"
    | "SYSTEM"
    | "PLAY_SE"
    | "HEAT"
    | "USER_LIST"
    | "USER_JOIN"
    | "USER_LEAVE"
    | "USER_VISIBILITY";
  text?: string;
  user?: string;
  seId?: string;
  value?: number;
  /** PLAY_SE のみ: 送信者の world 座標 (= 距離減衰計算用)。/club からの送信のみ
   *  含まれ、/ からの送信や旧クライアントでは undefined。 */
  x?: number;
  y?: number;
  // USER_LIST: unique name + count + 集約 visible
  participants?: Array<{ name: string; count: number; visible: boolean }>;
  // USER_VISIBILITY / USER_JOIN: 集約 visible
  visible?: boolean;
}

async function boot(): Promise<void> {
  const me = await fetchMe();
  let loggedIn = me !== null;

  const nameInput = document.getElementById("club-name") as HTMLInputElement;
  const aikotobaField = document.getElementById("club-aikotoba-field") as HTMLElement;
  const aikotobaInput = document.getElementById("club-aikotoba") as HTMLInputElement;
  const hintLogin = document.getElementById("club-hint-login") as HTMLElement;
  const inviteNoteEl = document.getElementById("club-invite-note") as HTMLElement;
  const errEl = document.getElementById("club-error") as HTMLParagraphElement;

  // 未ログイン状態でのみ ?invite= を試す (= ログイン済みなら合言葉欄が消えていて
  // prefill する場所がない)
  if (!loggedIn) {
    await tryPrefillInvite(aikotobaInput, inviteNoteEl, errEl);
  }

  // ログイン状態に応じて合言葉欄の出し入れ。表示名は、ログイン済みなら session の
  // 名前を prefill (= /club 内表示も同じ名前)、未ログインなら空のまま (= ユーザ入力)。
  function renderAuthState(): void {
    if (loggedIn && me) {
      nameInput.value = me.name;
      aikotobaField.hidden = true;
      aikotobaInput.required = false;
      hintLogin.hidden = true;
    } else {
      aikotobaField.hidden = false;
      aikotobaInput.required = true;
      hintLogin.hidden = false;
    }
  }
  renderAuthState();

  // アバター選択肢を AVATARS から動的生成。サムネ + ラジオで並べる。
  // デフォルトは AVATARS[0] (= 配列の最初の 1 件) が checked。
  // 選択値は submit ハンドラで `input[name="club-avatar"]:checked` から拾う。
  // AVATARS が空 (= 画像が 1 つも無い) ならフィールドごと隠す (= 棒人間 fallback で
  //  動く既存挙動)
  function renderAvatarOptions(): void {
    const container = document.getElementById("club-avatar-options");
    const pick = container?.closest(".club-avatar-pick") as HTMLElement | null;
    if (!container || !pick) return;
    if (AVATARS.length === 0) {
      pick.hidden = true;
      return;
    }
    container.replaceChildren();
    AVATARS.forEach((av, index) => {
      const label = document.createElement("label");
      label.className = "club-avatar-option";
      label.title = av.label ?? av.key;

      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "club-avatar";
      radio.value = av.key;
      if (index === 0) radio.checked = true;
      label.appendChild(radio);

      const img = document.createElement("img");
      img.src = av.url;
      img.alt = av.label ?? av.key;
      img.loading = "lazy";
      label.appendChild(img);

      const text = document.createElement("span");
      text.textContent = av.label ?? av.key;
      label.appendChild(text);

      container.appendChild(label);
    });
  }
  renderAvatarOptions();

  const form = document.getElementById("club-join-form") as HTMLFormElement;
  const joinBtn = document.getElementById("club-join") as HTMLButtonElement;
  const listenOnlyCheckbox = document.getElementById("club-listen-only") as HTMLInputElement;
  const listenOnlyNote = document.getElementById("club-listen-only-note") as HTMLElement;

  // 聞き専 opt-in の表示切替: 注意書きの開閉 + join ボタン文言を変える。
  // ボタン文言で「マイクが出るかどうか」が submit 前に視覚的にわかる。
  const refreshListenOnlyUi = () => {
    const on = listenOnlyCheckbox.checked;
    listenOnlyNote.hidden = !on;
    joinBtn.textContent = on ? "入室する (聞き専モード)" : "入室する (マイク許可が出ます)";
  };
  listenOnlyCheckbox.addEventListener("change", refreshListenOnlyUi);
  refreshListenOnlyUi();

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    joinBtn.disabled = true;
    errEl.textContent = "";

    const displayName = nameInput.value.trim();
    if (!displayName) {
      errEl.textContent = "表示名を入力してください";
      joinBtn.disabled = false;
      return;
    }

    // iOS Safari は「`getUserMedia` を await した後」だと user gesture コンテキストが
    // 失効して AudioContext.resume() が永久に suspended のままになる。
    // 解決策: submit ハンドラの先頭で同期的に AudioContext を作って resume() の
    // Promise だけ先に発行し、後で `await resumePromise` する。
    // (`webkitAudioContext` フォールバックは Safari 14 で削除済 = 2026 時点で dead code)
    const audioCtx = new AudioContext();
    const resumePromise: Promise<void> =
      audioCtx.state === "suspended" ? audioCtx.resume() : Promise.resolve();

    // 未ログインなら最初に /api/login で session cookie を発行する。
    // 失敗 (合言葉違い・通信エラー) なら audioCtx を閉じて return。
    if (!loggedIn) {
      const aikotoba = aikotobaInput.value;
      if (!aikotoba) {
        errEl.textContent = "合言葉を入力してください";
        joinBtn.disabled = false;
        void audioCtx.close().catch(() => undefined);
        return;
      }
      try {
        const res = await fetch("/api/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            name: displayName,
            aikotoba,
            // 招待 URL 経由なら invite token を含めて送る (= サーバが session.inviter
            //  に焼き込む)。/ 側 main.ts と同じ規約。
            ...(inviteToken ? { invite: inviteToken } : {}),
          }),
        });
        if (res.status === 401) {
          errEl.textContent = "合言葉が違います";
          joinBtn.disabled = false;
          void audioCtx.close().catch(() => undefined);
          return;
        }
        if (!res.ok) {
          errEl.textContent = `ログインに失敗しました (${res.status})`;
          joinBtn.disabled = false;
          void audioCtx.close().catch(() => undefined);
          return;
        }
        // 平文 aikotoba を DOM 上に残さない (= XSS / DOM dump 経路の最小化)
        aikotobaInput.value = "";
        loggedIn = true;
        // 招待 URL 経由でログインしたら URL から ?invite= を消す (= リロード時の
        //  リプレイ防止 + 共有のしすぎ防止)。/ 側 main.ts と同じ規約。
        if (inviteToken) {
          const cleanUrl = new URL(location.href);
          cleanUrl.searchParams.delete("invite");
          history.replaceState(null, "", cleanUrl.toString());
          inviteToken = null;
        }
      } catch {
        errEl.textContent = "通信エラー";
        joinBtn.disabled = false;
        void audioCtx.close().catch(() => undefined);
        return;
      }
    }

    const listenOnly = listenOnlyCheckbox.checked;
    let micStream: MediaStream;
    if (listenOnly) {
      // 聞き専モード: getUserMedia を呼ばない (= マイクアイコンが OS に出ない、
      // 録音権限を要求しない)。空の MediaStream を渡せば Net 側が voice mesh を
      // skip する。後で upgrade ボタンから talking に昇格できる。
      micStream = new MediaStream();
    } else {
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
      } catch (err) {
        errEl.textContent = "マイクの許可が取れませんでした。ブラウザ設定を確認して再度押してください。マイクなしで入室するなら下の「聞き専モード」にチェックを入れてください。";
        joinBtn.disabled = false;
        void audioCtx.close().catch(() => undefined);
        console.warn("[club] getUserMedia failed", err);
        return;
      }
    }

    // resume の完了を確認 (= 既に発行済みなのでここで await するだけ。
    // user gesture コンテキストは使い切っているが、resume の dispatch は同期で済んでいる)
    try { await resumePromise; } catch { /* ignore */ }

    const graph = createGraph(audioCtx, MAP.ambience);
    const live = new Live(audioCtx, graph);

    // ピア音声 (Net) は audioCtx を共有。zoneFilter / wetSend を通らない voiceBus に
    // 流すことで、BAR ゾーンでも声はクリアに聞こえる (= BGM だけこもる)。
    // 群衆減衰は music / voice それぞれの crowdLowShelf を setCrowdCount が
    // 同じ値で動かすので、クラブ全体の空気感は両 bus で共有される。
    // 入室前に選択された avatar key (= ラジオ button)。Net 経由で ?avatar= に乗せて
     // DO に渡し、他クライアントが正しいスプライトを描画できるようにする。
    const selectedAvatarKey =
      (document.querySelector(
        "input[name='club-avatar']:checked",
      ) as HTMLInputElement | null)?.value ?? null;

    let crowd = 0;
    // clubScene は後段で生成されるが、Net の onPeerListenStateChange ハンドラから
    // 参照するため先に `let` で宣言しておく (= TDZ 回避)。peer-listen-state は WS で
    // 非同期に届くので、scene が assign される前にハンドラが走るとしても optional
    // chaining で安全に skip できる (初期化中に他人が upgrade するレースは無視)。
    let clubScene: ClubScene | null = null;
    const net = new Net(audioCtx, graph.voiceBus, micStream, displayName, selectedAvatarKey, listenOnly, {
      onClose: () => setStatus("接続が切れました"),
      onCrowdChange: (n) => {
        crowd = n;
        live.setCrowdCount(n);
        updateHud({ peers: n });
      },
      // 個別ピアの play 失敗はクラブ全体の HUD には出さず、console.warn だけで充分。
      // (net.ts 内ですでに console.warn 済み = ここでは無視で良い)
      onAudioError: () => undefined,
      // peer の listen-only 状態が flip した時に scene の label を update。
      onPeerListenStateChange: (id, lo) => clubScene?.refreshPeerListenLabel(id, lo),
    });

    try {
      await net.start();
    } catch (err) {
      errEl.textContent =
        "クラブへの接続に失敗しました。/ にログインしてから再度試してください。";
      joinBtn.disabled = false;
      // 失敗パスではマイクのトラックを止めて、ブラウザの録音インジケータを消す。
      // これをやらないと「Stop してないマイク」をブラウザが掴んだままになり、
      // ユーザーが「録音されているのか」と不安になる + リソースリーク
      micStream.getTracks().forEach((t) => t.stop());
      // 失敗パスでは AudioContext を解放する (= 起動したまま leak しない)
      void audioCtx.close().catch(() => undefined);
      console.warn("[club] net.start failed", err);
      return;
    }

    // WHEP は失敗しても入室は続行。
    // "unstable" = pc.connectionState === "disconnected" の grace 中 (= ICE restart で
    // 復旧の余地あり)。"connecting" と同じく「過渡」扱いにして "OFF" を表示しない。
    live.onStatus = (state) => {
      const el = document.getElementById("hud-live");
      if (!el) return;
      if (state === "connected") el.textContent = "ON";
      else if (state === "connecting" || state === "unstable") el.textContent = "...";
      else el.textContent = "OFF";
    };
    // <audio> の play() が autoplay policy 等で失敗したときに HUD で通知。
    // ライブ音声が無音になるのを「気付かれない無音」にしないための保険。
    live.onAudioError = () => {
      const el = document.getElementById("hud-live");
      if (el) el.textContent = "ERR";
      setStatus("ライブ音声の再生に失敗しました (ブラウザの autoplay 制限)");
    };
    void live.connect();

    // UI を入室済み状態に切り替え
    (document.getElementById("club-overlay") as HTMLElement).hidden = true;
    (document.getElementById("club-hud") as HTMLElement).hidden = false;
    setupShareButton();
    // 名前変更 popover (= /、/lite と共通の UX、reload で全 state 再初期化)
    setupNameChangeModal(displayName);
    // ChatRoom (/ws) に繋いで chat + SE + HEAT を有効化。SE preload は内部で
    // 非同期に進む (= 待たないので入室体感が遅れない)。
    // self 座標 getter を setupChatAndSE に渡す → ホーン送信時 (= 座標を message に
    // 同梱) と受信時 (= 送信者との距離で gain 計算) で使う。
    getSelfPos = () => (net.me ? { x: net.me.x, y: net.me.y } : null);
    setupChatAndSE(audioCtx);
    setStatus(`as ${displayName} / id ${(net.me?.id ?? "").slice(0, 6)}`);

    // dev / opt-in observability hook。本番ビルドでは
    // `__clubDebugEnable` が明示的に立っていない限り何も露出しない。
    // コンソールから `__clubDebug.graph.musicLimiter.reduction` などで
    // limiter / per-peer compressor の reduction を読めるようにするためのフック。
    //   有効化方法:
    //     1. dev ビルド (`vite dev` → `import.meta.env.DEV === true`) なら常時有効
    //     2. 本番でも `localStorage.setItem('__clubDebugEnable', '1')` で opt-in
    //     3. もしくは DevTools で `window.__clubDebugEnable = true` を打ってから入室
    // `import.meta.env.DEV` は vite が **ビルド時に true/false リテラルへ静的置換**
    // するので、本番ビルドではこのブロック全体が dead-code elimination で消える。
    // `src/client/vite-env.d.ts` で `vite/client` の型を入れているので any キャスト不要。
    //
    // **net を直接出さない**: peer の MediaStream / RTCPeerConnection は機微情報
    // (他人の音声 raw access に到達可能) なので、デバッグ用 view は peer 数 / id
    // までに絞る。limiter の reduction など本 PR の主目的は graph / live で読める。
    interface ClubDebugView {
      graph: typeof graph;
      live: typeof live;
      /** `activePeerCount` は voice dynamic disconnect (D4) の field 観測用。
       *  半径内 (= isActive=true) の peer 数 = 実際に Web Audio graph に
       *  繋がっている数。これと peerCount の比で「半径フィルタの効き目」を判定。 */
      netInfo: () => { peerCount: number; activePeerCount: number; peerIds: string[] };
    }
    interface ClubDebugWindow extends Window {
      __clubDebug?: ClubDebugView;
      __clubDebugEnable?: boolean;
    }
    const w = window as ClubDebugWindow;
    const isDev = import.meta.env.DEV;
    const enabledByStorage = (() => {
      try { return localStorage.getItem("__clubDebugEnable") === "1"; }
      catch { return false; }
    })();
    if (isDev || w.__clubDebugEnable || enabledByStorage) {
      w.__clubDebug = {
        graph,
        live,
        netInfo: () => ({
          peerCount: net.peers.size,
          activePeerCount: net.getActivePeerCount(),
          peerIds: Array.from(net.peers.keys()),
        }),
      };
    }

    // 照明モードを **Phaser scene 構築前** に localStorage から読み出す。
    // scene constructor に dep として渡すことで、setupLightingButton が
    // scene.getLightingMode() を読んだ瞬間から正しい値を返す (= button UI と
    // 実際の lighting 状態が常に一致、init race なし)。
    let initialLightingMode: "off" | "lite" | "full" = "off";
    try {
      const saved = localStorage.getItem("__clubLightingMode");
      if (saved === "lite" || saved === "full") initialLightingMode = saved;
    } catch {
      // localStorage 不在 (= private mode 等) は OFF のまま
    }

    clubScene = new ClubScene({
      net,
      live,
      initialLightingMode,
      onZoneChange: (zone) => {
        updateHud({ zone, crowdDb: live.getCrowdDb() });
      },
      // 入室前にラジオで選択された avatar key。未選択時は null で
      //  scene 側が hash 決定論にフォールバックする。
      // Net コンストラクタにも同じ値を渡している (= サーバ broadcast 用)。
      selfAvatarKey: selectedAvatarKey,
    });
    const scene = clubScene;

    new Phaser.Game({
      type: Phaser.AUTO,
      parent: "club-game",
      backgroundColor: "#0f1115",
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      physics: {
        default: "arcade",
        arcade: { gravity: { x: 0, y: 0 }, debug: false },
      },
      scene,
    });

    // モバイルユーザ向け zoom ボタン。デスクトップでもクリック可能。
    document.getElementById("club-zoom-in")?.addEventListener("click", () => {
      scene.zoomIn();
    });
    document.getElementById("club-zoom-out")?.addEventListener("click", () => {
      scene.zoomOut();
    });

    // reactive lighting (音域反応 floor 照明) の opt-in トグル。
    // click で OFF → Lite (低域のみ) → Full (低/中/高 + 暗化) → OFF を循環する。
    // 光過敏症対策で初期は OFF (localStorage 保存値があれば scene の create() で復元済み)。
    setupLightingButton(scene);

    // Club Acoustic Simulator の ON/OFF toggle (= 「直挿しイヤホン」⇆「クラブで聞いてる」
    // 体感切替)。desktop のみ UI を出す。モバイルは UI 非表示 + audio node 自体が
    // graph に存在しない (= AudioContext にノードが乗らない、live.ts も skip)。
    setupClubAcoustic(audioCtx, graph);

    // listenOnly で入った人向け: 「🎤 マイクで参加する」ボタンを HUD に出して、
    // クリック時に getUserMedia → net.upgradeToTalking → mute ボタンに置き換える。
    const baseStatus = `as ${displayName} / id ${(net.me?.id ?? "").slice(0, 6)}`;
    if (net.listenOnly) {
      // 聞き専中は status バーに inline indicator を出して「自分は声を出していない」
      // を継続的に視認できるようにする (Game programmer review low)。upgrade 成功時に
      // setupUpgradeMicButton が baseStatus に戻す。
      setStatus(`${baseStatus} (🔇 聞き専中)`);
      setupUpgradeMicButton(net, audioCtx, scene, baseStatus);
    } else {
      setupMuteButton(net);
    }

    // 初期 HUD 描画
    updateHud({ peers: crowd, zone: null, crowdDb: 0 });
  });
}

function setupLightingButton(scene: import("./scene").ClubScene): void {
  const btn = document.getElementById("club-lighting") as HTMLButtonElement | null;
  if (!btn) return;
  // モバイル端末 (≤768px) では Full モードの 4 枚 ADD blend overlay が GPU 負荷で
  // fps 影響を出しやすいので、初期 OFF 状態の title で Lite を推奨表示する。
  // matchMedia は SSR 不在 / 不要環境では fallback false で安全。
  const isLikelyMobile = typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 768px)").matches
    : false;
  const render = () => {
    const mode = scene.getLightingMode();
    btn.textContent =
      mode === "off" ? "💡 OFF" : mode === "lite" ? "💡 Lite" : "💡 Full";
    const mobileHint = isLikelyMobile && mode === "off" ? " (モバイル端末は Lite 推奨)" : "";
    btn.title =
      mode === "off"
        ? `照明 OFF (光過敏症対策で既定)${mobileHint}。クリックで Lite に切替`
        : mode === "lite"
          ? "照明 Lite (低域 + 暗化)。クリックで Full に切替"
          : "照明 Full (低/中/高 + 暗化)。クリックで OFF に切替";
  };
  render();
  btn.addEventListener("click", () => {
    const next: "off" | "lite" | "full" =
      scene.getLightingMode() === "off"
        ? "lite"
        : scene.getLightingMode() === "lite"
          ? "full"
          : "off";
    scene.setLightingMode(next);
    render();
  });
}

// 招待 URL (`/club?invite=<token>`) を踏んで来たゲスト向けに、起動時に呼ぶ。
// 成功時は inviteToken に値が入り、submit ハンドラが /api/login に乗せて送る。
async function tryPrefillInvite(
  aikotobaInput: HTMLInputElement,
  noteEl: HTMLElement,
  errEl: HTMLElement,
): Promise<void> {
  const token = new URL(location.href).searchParams.get("invite");
  if (!token) return;
  try {
    const res = await fetch("/api/invite-decrypt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ invite: token }),
    });
    if (!res.ok) {
      if (res.status === 401) {
        errEl.textContent =
          "招待リンクが無効か期限切れです。合言葉を直接入力してください。";
      }
      return;
    }
    const data = (await res.json()) as { aikotoba: string; inviter: string };
    inviteToken = token;
    aikotobaInput.value = data.aikotoba;
    aikotobaInput.readOnly = true;
    aikotobaInput.classList.add("readonly");
    noteEl.textContent =
      `🎟️ ${data.inviter} さんからの招待で入室します。お名前だけ入れて「入室する」を押してください。`;
    noteEl.hidden = false;
  } catch {
    // ネットワークエラーは通常 login に silently フォールバック
  }
}

// 入室成功後に topbar の 🎟️ 共有ボタンを生やす。click で /api/invite を叩いて
// `/club?invite=<token>` 形式の URL を modal で表示。
function setupShareButton(): void {
  const btn = document.getElementById("club-share-btn") as HTMLButtonElement | null;
  if (!btn) return;
  // 二重 attach 防止 (= 念のため、再入室の経路があった場合用)
  if (btn.dataset.wired === "1") {
    btn.hidden = false;
    return;
  }
  btn.dataset.wired = "1";
  btn.hidden = false;
  btn.addEventListener("click", handleShareClick);
}

async function handleShareClick(): Promise<void> {
  const btn = document.getElementById("club-share-btn") as HTMLButtonElement;
  btn.disabled = true;
  const original = btn.textContent;
  btn.textContent = "発行中…";
  try {
    const res = await fetch("/api/invite", {
      method: "POST",
      credentials: "same-origin",
    });
    if (!res.ok) {
      alert(`招待リンクを発行できませんでした (${res.status})`);
      return;
    }
    const data = (await res.json()) as { token: string; expiresAt: number };
    const inviteUrl = `${location.origin}/club?invite=${encodeURIComponent(data.token)}`;
    showShareModal(inviteUrl, new Date(data.expiresAt * 1000));
  } catch {
    alert("通信エラーで招待リンクを発行できませんでした");
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}

function showShareModal(url: string, expiresAt: Date): void {
  const modal = document.getElementById("club-share-modal") as HTMLElement;
  const urlEl = document.getElementById("club-share-url") as HTMLInputElement;
  const expEl = document.getElementById("club-share-expires") as HTMLElement;
  const copyBtn = document.getElementById("club-share-copy") as HTMLButtonElement;
  const closeBtn = document.getElementById("club-share-close") as HTMLButtonElement;

  urlEl.value = url;
  expEl.textContent = `有効期限: ${expiresAt.toLocaleString()}`;
  modal.hidden = false;
  copyBtn.textContent = "コピー";
  copyBtn.disabled = false;

  // onclick への代入で上書きするので、何度開いてもリスナーが累積しない
  copyBtn.onclick = async () => {
    try {
      await navigator.clipboard.writeText(url);
      copyBtn.textContent = "✓ コピーしました";
    } catch {
      urlEl.select();
      copyBtn.textContent = "↑ URL を選択しました";
    }
  };
  closeBtn.onclick = () => {
    modal.hidden = true;
  };
}

// ChatRoom (= /ws) に繋いで chat + SE + HEAT を有効化。/club では空間音響と
// 並列で動かす (= SE は destination 直結で空間化なし、PR-C で空間化検討)。
function setupChatAndSE(audioCtx: AudioContext): void {
  seAudioCtx = audioCtx;
  // SE → seGain → destination。空間音響 graph (musicBus / voiceBus) はバイパス
  // するので reverb / zoneFilter は掛からない。SE は「全員同時の合図」なので
  // 個人の立ち位置や zone で減衰しない方が体感が良い。
  seGain = audioCtx.createGain();
  seGain.gain.value = SE_GAIN_DEFAULT;
  seGain.connect(audioCtx.destination);
  void preloadSEs(audioCtx);

  const panel = document.getElementById("club-chat-panel");
  if (panel) panel.hidden = false;

  // 参加者リスト (= /、/lite と共通の USER_LIST / USER_VISIBILITY を消費)
  const listEl = document.getElementById("club-participants-list") as HTMLElement | null;
  const countEl = document.getElementById("club-participants-count") as HTMLElement | null;
  if (listEl) participantsList = new ParticipantsList(listEl, countEl);

  connectChatWs();

  const form = document.getElementById("club-chat-form") as HTMLFormElement | null;
  const input = document.getElementById("club-chat-input") as HTMLInputElement | null;
  if (form && input) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text || chatWs?.readyState !== WebSocket.OPEN) return;
      chatWs.send(JSON.stringify({ type: "CHAT", text }));
      input.value = "";
    });
  }

  const hornBtn = document.getElementById("club-horn-btn") as HTMLButtonElement | null;
  hornBtn?.addEventListener("click", () => {
    if (chatWs?.readyState !== WebSocket.OPEN) return;
    // 自分の座標を一緒に送る (= 受信者が距離減衰を計算するため)。/club の
    // この場で getSelfPos は必ず net.me から有効値を返すが、防御的に null チェック。
    const pos = getSelfPos?.();
    const payload = pos
      ? { type: "PLAY_SE", seId: "horn", x: Math.round(pos.x), y: Math.round(pos.y) }
      : { type: "PLAY_SE", seId: "horn" };
    chatWs.send(JSON.stringify(payload));
  });
}

// /ws (ChatRoom) WebSocket 接続 + reconnect 経路。
// `/` 側 main.ts と同じ「visibility ガード + exponential backoff」を踏襲。
let chatWsBackoffMs = 5000;
let chatWsVisibilityWaiter: (() => void) | null = null;

function connectChatWs(): void {
  if (document.visibilityState === "hidden") {
    if (chatWsVisibilityWaiter) return;
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onVisible);
      chatWsVisibilityWaiter = null;
      connectChatWs();
    };
    chatWsVisibilityWaiter = onVisible;
    document.addEventListener("visibilitychange", onVisible);
    return;
  }

  const proto = location.protocol === "https:" ? "wss" : "ws";
  chatWs = new WebSocket(`${proto}://${location.host}/ws`);
  chatWs.addEventListener("open", () => {
    chatWsBackoffMs = 5000;
    // visibility tracker は 1 度だけ生成、再接続のたびに現在状態を再送
    if (!visibilityTracker) {
      visibilityTracker = startVisibilityTracker((msg) => {
        if (chatWs?.readyState === WebSocket.OPEN) chatWs.send(JSON.stringify(msg));
      });
    }
    visibilityTracker.notifyWsOpen();
  });
  chatWs.addEventListener("message", (ev) => {
    let msg: ChatServerMessage;
    try {
      msg = JSON.parse(ev.data as string) as ChatServerMessage;
    } catch {
      return;
    }
    handleChatMessage(msg);
  });
  chatWs.addEventListener("close", () => {
    chatWs = null;
    const wait = chatWsBackoffMs + Math.floor(Math.random() * 2000);
    setTimeout(connectChatWs, wait);
    chatWsBackoffMs = Math.min(chatWsBackoffMs * 1.5, 30000);
  });
}

function handleChatMessage(msg: ChatServerMessage): void {
  if (msg.type === "CHAT") {
    appendChatMessage("chat", `${msg.user ?? "anon"}: ${msg.text ?? ""}`);
  } else if (msg.type === "SYSTEM") {
    appendChatMessage("system", msg.text ?? "");
  } else if (msg.type === "PLAY_SE" && msg.seId) {
    // x/y がサーバ broadcast に含まれていれば送信者位置として距離減衰を適用。
    const senderPos =
      typeof msg.x === "number" && typeof msg.y === "number" &&
      Number.isFinite(msg.x) && Number.isFinite(msg.y)
        ? { x: msg.x, y: msg.y }
        : null;
    playSE(msg.seId, senderPos);
  } else if (msg.type === "HEAT" && typeof msg.value === "number") {
    updateHeatMeter(msg.value);
  } else if (
    msg.type === "USER_LIST" ||
    msg.type === "USER_JOIN" ||
    msg.type === "USER_LEAVE" ||
    msg.type === "USER_VISIBILITY"
  ) {
    participantsList?.handleMessage(msg);
  }
}

function appendChatMessage(kind: "chat" | "system", text: string): void {
  const list = document.getElementById("club-messages");
  if (!list) return;
  const el = document.createElement("div");
  el.className = `club-msg club-msg--${kind}`;
  el.textContent = text;
  list.appendChild(el);
  while (list.childElementCount > 50) list.firstElementChild?.remove();
  list.scrollTop = list.scrollHeight;
}

function updateHeatMeter(value: number): void {
  const meter = document.getElementById("club-heat-meter") as HTMLElement | null;
  const fill = document.getElementById("club-heat-fill");
  const valEl = document.getElementById("club-heat-val");
  if (!meter || !fill || !valEl) return;
  meter.hidden = false;
  const clamped = Math.max(0, Math.min(100, value));
  (fill as HTMLElement).style.width = `${clamped}%`;
  (fill as HTMLElement).style.background = `hsl(${220 - clamped * 2.2}, 85%, 55%)`;
  valEl.textContent = String(clamped);
}

async function preloadSEs(audioCtx: AudioContext): Promise<void> {
  await Promise.all(
    (Object.entries(SE_FILES) as [SEId, string][]).map(async ([id, url]) => {
      try {
        const res = await fetch(url, { cache: "force-cache" });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const arr = await res.arrayBuffer();
        const buf = await audioCtx.decodeAudioData(arr);
        seBuffers.set(id, buf);
      } catch (e) {
        console.warn(`[club] SE preload failed for ${id}:`, e);
      }
    }),
  );
}

// /club では SE は **destination 直結** (= 空間音響 graph をバイパス)。
// 距離による gain 減衰だけ追加: 送信者位置 senderPos が分かれば inverse rolloff
// で短期的な per-tap GainNode を挟んでから seGain に流す。pan は付けない
// (= 全員に同じ stereo 像、距離による「遠い人のホーンは小さい」だけ)。
function playSE(seId: string, senderPos: { x: number; y: number } | null = null): void {
  if (!seAudioCtx || !seGain) return;
  const buf = seBuffers.get(seId);
  if (!buf) return;
  const src = seAudioCtx.createBufferSource();
  src.buffer = buf;
  // 距離減衰: senderPos と自分の位置が両方ある場合のみ計算。それ以外は素通し。
  const myPos = getSelfPos?.() ?? null;
  let distGain: GainNode | null = null;
  if (senderPos && myPos) {
    const d = Math.hypot(senderPos.x - myPos.x, senderPos.y - myPos.y);
    const g = Math.max(HORN_DIST_MIN_GAIN, HORN_DIST_REF / (HORN_DIST_REF + d * HORN_DIST_ROLLOFF));
    distGain = seAudioCtx.createGain();
    distGain.gain.value = g;
    src.connect(distGain);
    distGain.connect(seGain);
  } else {
    src.connect(seGain);
  }
  src.start(0);
  // src は ended で自動 GC、distGain も disconnect 不要 (= src.disconnect() で
  // ぶら下がりが切れ、参照が無くなり次第 GC される)。
}

/** Club Acoustic Simulator の master toggle UI handler。
 *  - desktop: checkbox で graph.clubAcousticEnabled.gain を 0/1 に setTargetAtTime ramp。
 *    localStorage で永続化、default ON。
 *  - mobile: UI ブロック自体を hidden、checkbox 操作不可。audio node は graph に存在しない。
 *  位置依存パラメータ (= subPresenceBlend / hfReflectionBlend / cornerBassShelf) は
 *  live.ts の updatePlayerPos が毎フレーム modulate するのでここでは触らない。 */
function setupClubAcoustic(audioCtx: AudioContext, graph: ClubGraph): void {
  const containerEl = document.getElementById("club-acoustic") as HTMLElement | null;
  const toggleEl = document.getElementById("club-acoustic-toggle") as HTMLInputElement | null;
  const noteEl = document.getElementById("club-acoustic-mobile-note") as HTMLElement | null;
  if (!containerEl || !toggleEl) return;

  // mobile: UI ブロックごと隠し、explanation note だけ残す。audio nodes も生成されてない。
  if (graph.isMobile || !graph.clubAcousticEnabled) {
    toggleEl.checked = false;
    toggleEl.disabled = true;
    // toggle row を隠して、mobile 用の note だけ表示する。
    const row = toggleEl.closest(".club-acoustic__row") as HTMLElement | null;
    if (row) row.hidden = true;
    if (noteEl) noteEl.hidden = false;
    return;
  }

  // localStorage 復元 (default ON = "1"、key 未設定でも ON)
  let initialOn = true;
  try {
    const raw = localStorage.getItem("__clubAcousticEnabled");
    if (raw === "0") initialOn = false;
  } catch { /* ignore */ }

  // graph 内の setClubAcousticEnabled が ramp + disconnect を内部で管理
  // (= OFF 時に上流 WaveShaper / DelayNode を切り離して CPU 完全カット)
  const applyEnabled = (on: boolean): void => {
    graph.setClubAcousticEnabled?.(on);
  };

  toggleEl.checked = initialOn;
  applyEnabled(initialOn);
  toggleEl.addEventListener("change", () => {
    const on = toggleEl.checked;
    applyEnabled(on);
    try { localStorage.setItem("__clubAcousticEnabled", on ? "1" : "0"); } catch { /* ignore */ }
  });
}

function setupMuteButton(net: Net): void {
  const btn = document.getElementById("club-mute") as HTMLButtonElement;
  btn.hidden = false;
  const refresh = () => {
    btn.textContent = net.muted ? "🎤 OFF" : "🎤 ON";
    btn.setAttribute("aria-pressed", String(net.muted));
    btn.classList.toggle("club-mute--off", net.muted);
  };
  btn.addEventListener("click", () => {
    net.setMicMuted(!net.muted);
    refresh();
  });
  refresh();
}

/** 聞き専で入った人が後からマイクで参加するためのボタン。
 *  click → getUserMedia → net.upgradeToTalking → 自分の label の 🔇 を消す
 *  → mute ボタンに置き換える。失敗時は status バーに inline 表示してボタン続行。 */
function setupUpgradeMicButton(
  net: Net,
  audioCtx: AudioContext,
  scene: import("./scene").ClubScene,
  baseStatus: string,
): void {
  const btn = document.getElementById("club-upgrade-mic") as HTMLButtonElement;
  btn.hidden = false;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    // user gesture を保持するため、await 前に resume() の Promise だけ先発行
    // (iOS Safari の autoplay policy 対応、Browser API review medium)。
    // submit ハンドラ側 (resumePromise 先発行) と同じパターン。
    const resumePromise: Promise<void> =
      audioCtx.state === "suspended" ? audioCtx.resume() : Promise.resolve();
    let micStream: MediaStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
    } catch (err) {
      // 拒否 / 他アプリ占有などは inline status で通知 (modal alert は mobile で
      // タブをブロックする、Browser API review medium)。ユーザは再度ボタンを押せる。
      setStatus("⚠️ マイクの許可が取れませんでした。ブラウザ設定を確認して再度押してください");
      btn.disabled = false;
      console.warn("[club] upgrade getUserMedia failed", err);
      return;
    }
    // resume() の完了を確認 (= 既に発行済みなので await するだけ)。
    try { await resumePromise; } catch { /* ignore */ }
    if (audioCtx.state !== "running") {
      // resume 失敗時の field observability (SRE review low)
      console.warn("[club] upgrade proceeding with audioCtx.state =", audioCtx.state);
    }
    try {
      await net.upgradeToTalking(micStream);
    } catch (err) {
      // upgrade 失敗 (= まれ): track stop + status 通知
      console.warn("[club] upgradeToTalking failed", err);
      micStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      setStatus("⚠️ voice mesh への参加に失敗しました。リロードしてから入室し直してください");
      btn.disabled = false;
      return;
    }
    // 自分の label の 🔇 を取り除く
    scene.refreshSelfListenLabel(false);
    // status バーから聞き専 indicator を消して通常状態に戻す
    setStatus(baseStatus);
    // upgrade 成功: ボタンを mute ボタンに置き換える
    btn.hidden = true;
    setupMuteButton(net);
  });
}

function updateHud(state: { peers?: number; zone?: Zone | null; crowdDb?: number }): void {
  if (state.peers !== undefined) {
    const el = document.getElementById("hud-peers");
    if (el) el.textContent = String(state.peers);
  }
  if (state.zone !== undefined) {
    const el = document.getElementById("hud-zone");
    if (el) el.textContent = state.zone?.name ?? "-";
  }
  if (state.crowdDb !== undefined) {
    const el = document.getElementById("hud-crowd");
    if (el) el.textContent = `${state.crowdDb.toFixed(1)} dB`;
  }
}

boot().catch((err) => {
  console.error("[club] boot failed", err);
  setStatus("起動エラー");
});
